import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Loader2, Save, LogIn, Check, X, RefreshCw, Users, Eye as EyeIcon, MessageCircle, FileDown, Bell, Euro, Settings as SettingsIcon, KeyRound, Trash2, Upload, FileText, Video as VideoIcon, Presentation, Pencil, PlusCircle, Clock, Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Statuses that require admin action (both pending payment and receipt uploaded)
const PENDING_STATUSES = new Set(["pending", "receipt_uploaded"]);

// Short "bling" tone (base64 wav ~0.2s, a rising 2-tone chime) played on new registration
const CHIME_MP3 = "data:audio/mp3;base64,//NExAAOkP4YAUpAADwAAqSSJJIkjLLLLLLLTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTT/////////////////////////////8AAAA5TEFNRTMuMTAwAc0AAAAAAAAAABQCJANrTgAAzAAAAAAgHAgYIAAAA";

export default function Admin() {
  const { t } = useLang();
  const [token, setToken] = useState(localStorage.getItem("sseuropa_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [regs, setRegs] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkItems, setBulkItems] = useState(null); // null = not yet generated; array = result
  const [bulkLoading, setBulkLoading] = useState(false);
  const [groupBySlot, setGroupBySlot] = useState(false);
  const [slotsMap, setSlotsMap] = useState({});
  const [visitors, setVisitors] = useState({ visitors: [], total: 0, today: 0, week: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("todo");
  const [tab, setTab] = useState("regs");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [settings, setSettings] = useState({ admin_phone: "", admin_email: "", notify_on_new_registration: true });
  const prevPendingCount = useRef(0);
  const lastNotifTs = useRef(new Date().toISOString());
  const chimeRef = useRef(null);
  const originalTitleRef = useRef(typeof document !== "undefined" ? document.title : "");

  const headers = { "X-Admin-Token": token };

  // Play a short "bling" sound and flash the browser title on new registration
  const alertAdminBrowser = (reg) => {
    // 1) Audio ping
    try {
      if (!chimeRef.current) chimeRef.current = new Audio(CHIME_MP3);
      chimeRef.current.currentTime = 0;
      chimeRef.current.volume = 0.7;
      chimeRef.current.play().catch((e) => console.debug("Chime autoplay blocked:", e?.message));
    } catch (e) { console.debug("Chime init failed:", e); }
    // 2) Browser Notification (if permission granted)
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try {
        new Notification("🔔 Nuova iscrizione SS Europa", {
          body: `${reg.name || "Nuovo utente"} • Corso ${reg.orario_corso || ""}`,
          icon: "/chikoo_mascot.png",
          tag: `reg-${reg.reg_id}`,
        });
      } catch (e) { console.debug("Notification failed:", e); }
    }
    // 3) Flash browser tab title
    let flashCount = 0;
    const orig = originalTitleRef.current || "Admin SS Europa";
    const iv = setInterval(() => {
      document.title = flashCount % 2 === 0 ? "🔔 NUOVA ISCRIZIONE!" : orig;
      flashCount++;
      if (flashCount >= 8) { clearInterval(iv); document.title = orig; }
    }, 700);
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [r, v, s, sl] = await Promise.all([
        axios.get(`${API}/admin/registrations`, { headers }),
        axios.get(`${API}/admin/visitors`, { headers }),
        axios.get(`${API}/admin/settings`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/slots`, { headers }).catch(() => ({ data: [] })),
      ]);
      // Detect new pending registrations for a toast notification
      const newPending = (r.data || []).filter((x) => PENDING_STATUSES.has(x.approval_status)).length;
      if (silent && newPending > prevPendingCount.current) {
        const diff = newPending - prevPendingCount.current;
        toast.success(`${diff} nuova/e iscrizione/i da approvare!`, { duration: 6000 });
      }
      prevPendingCount.current = newPending;
      setRegs(r.data);
      setVisitors(v.data);
      if (s.data) setSettings(s.data);
      const map = {};
      (sl.data || []).forEach((x) => { map[x.id] = x; });
      setSlotsMap(map);
      setLastRefresh(new Date());
      localStorage.setItem("sseuropa_admin_token", token);
      setAuthed(true);
    } catch {
      if (!silent) toast.error("Token non valido");
    } finally { if (!silent) setLoading(false); }
  };

  // Poll notifications endpoint every 20s (real-time "bling" on new registration)
  const pollNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notifications`, {
        headers, params: { since: lastNotifTs.current },
      });
      lastNotifTs.current = data.server_time || lastNotifTs.current;
      for (const n of data.notifications || []) {
        if (n.type === "new_registration") {
          alertAdminBrowser(n);
          toast.info(`🔔 ${n.name} si è appena iscritto (corso ${n.orario_corso})`, { duration: 10000 });
        }
      }
    } catch (e) { console.debug("Notification poll failed:", e?.message); }
  };

  // Auto-refresh every 15 seconds + notification poll every 20s
  useEffect(() => {
    if (!authed) return;
    const iv1 = setInterval(() => { load(true); }, 15000);
    const iv2 = setInterval(pollNotifications, 20000);
    // Ask permission on first auth
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    return () => { clearInterval(iv1); clearInterval(iv2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, token]);

  const saveSettings = async () => {
    try {
      const { data } = await axios.put(`${API}/admin/settings`, settings, { headers });
      setSettings(data);
      toast.success("Impostazioni notifiche salvate");
    } catch { toast.error("Errore salvataggio"); }
  };

  const togglePayment = async (id, kind, paid) => {
    try {
      await axios.put(`${API}/admin/registrations/${id}/payment`, { kind, paid }, { headers });
      toast.success(paid ? `Quota ${kind === "corso" ? "corso €30" : "socio €20"} segnata come pagata` : "Segnata come non pagata");
      load(true);
    } catch { toast.error("Errore"); }
  };

  const approve = async (id) => {
    try {
      await axios.put(`${API}/admin/registrations/${id}/approve`, {}, { headers });
      toast.success("Approvata");
      load(false);
    } catch { toast.error("Errore"); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Motivo del rifiuto (opzionale):") || "";
    try {
      await axios.put(`${API}/admin/registrations/${id}/reject`, { reason }, { headers });
      toast.success("Rifiutata");
      load(false);
    } catch { toast.error("Errore"); }
  };

  const changeSlot = async (id, newSlot) => {
    try {
      await axios.put(`${API}/admin/registrations/${id}/course-slot`,
        { orario_corso: newSlot }, { headers });
      toast.success(newSlot === "entrambi" ? "Doppio corso concesso" : `Orario aggiornato: ${newSlot}`);
      load(true);
    } catch { toast.error("Errore aggiornamento orario"); }
  };

  const downloadPdf = (id) => {
    // Admin download uses admin token via query param (browser can't set custom headers on plain link)
    window.open(`${API}/registrations/${id}/pdf?admin_token=${encodeURIComponent(token)}`, "_blank");
  };

  const renameUser = async (id, currentNome, currentCognome) => {
    const nome = window.prompt("Nuovo nome:", currentNome || "");
    if (nome === null) return;
    const cognome = window.prompt("Nuovo cognome:", currentCognome || "");
    if (cognome === null) return;
    if (!nome.trim() || !cognome.trim()) { toast.error("Nome e cognome non possono essere vuoti"); return; }
    try {
      await axios.put(`${API}/admin/registrations/${id}/rename`, { nome: nome.trim(), cognome: cognome.trim() }, { headers });
      toast.success("Nome aggiornato");
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rinomina");
    }
  };


  const deleteRegistration = async (id, name) => {
    if (!window.confirm(`Vuoi CANCELLARE definitivamente l'iscrizione di ${name}?\n\nQuesta azione è irreversibile.`)) return;
    try {
      await axios.delete(`${API}/admin/registrations/${id}`, { headers });
      toast.success("Iscrizione cancellata");
      load(true);
    } catch { toast.error("Errore cancellazione"); }
  };


  const setUserPassword = async (id, name) => {
    const newPw = window.prompt(
      `Imposta / resetta la password per ${name || "questo utente"}\n(minimo 6 caratteri):`,
      ""
    );
    if (newPw === null) return;
    if (!newPw || newPw.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    try {
      const { data } = await axios.put(
        `${API}/admin/registrations/${id}/set-password`,
        { new_password: newPw },
        { headers }
      );
      const verb = data.action === "created" ? "creato" : "aggiornata";
      toast.success(
        `Password ${verb} per matricola ${data.matricola}. Condividi le credenziali con l'utente.`,
        { duration: 10000 }
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore impostazione password";
      toast.error(msg);
    }
  };

  const runBulkGenerate = async (only_missing = true) => {
    setBulkLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/bulk-generate-passwords`,
        { only_missing },
        { headers }
      );
      setBulkItems(data.items || []);
      toast.success(`Generate ${data.count} password temporanee`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore generazione in blocco");
    } finally { setBulkLoading(false); }
  };

  const printBulk = () => {
    const w = window.open("", "_blank", "noopener,noreferrer");
    if (!w) { toast.error("Il browser ha bloccato la finestra di stampa"); return; }
    const rows = (bulkItems || [])
      .filter((it) => it.password)
      .map(
        (it) => `
          <tr>
            <td class="mat">${it.matricola}</td>
            <td>${(it.nome || "") + " " + (it.cognome || "")}</td>
            <td>${it.cellulare || ""}</td>
            <td class="pw">${it.password}</td>
          </tr>`
      )
      .join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Credenziali SS Europa</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; padding: 24px; color: #0F1E3D; }
        h1 { color: #0B3D91; margin: 0 0 4px; }
        .sub { color: #5A6A94; font-size: 13px; margin-bottom: 18px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        th, td { padding: 10px 8px; border-bottom: 1px solid #E4EAF7; text-align: left; font-size: 13px; }
        th { background: #EEF4FF; color: #0B3D91; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; }
        .mat, .pw { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-weight: 700; color: #0B3D91; font-size: 14px; }
        .pw { background: #FFF7ED; color: #B45309; padding: 4px 8px; border-radius: 4px; }
        .note { margin-top: 18px; padding: 10px 12px; background: #FFF7E6; border-left: 3px solid #F97316; font-size: 12px; color: #B45309; }
        @media print { .no-print { display: none; } body { padding: 10px; } }
      </style></head><body>
        <h1>Credenziali temporanee — SS Europa by Amei</h1>
        <div class="sub">Generato il ${new Date().toLocaleString("it-IT")}. Password temporanee: gli studenti dovranno cambiarle al primo accesso.</div>
        <table>
          <thead><tr><th>Matricola</th><th>Nome</th><th>Cellulare</th><th>Password temporanea</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="note">⚠️ Tratta questo documento come un'informazione sensibile. Consegna ogni riga personalmente allo studente.</div>
        <div class="no-print" style="margin-top:18px"><button onclick="window.print()">🖨️ Stampa</button></div>
      </body></html>`;
    w.document.write(html);
    w.document.close();
  };

  if (!authed) {
    return (
      <div className="page-bg min-h-[70vh] flex items-center justify-center px-4">
        <div className="card-elevated p-8 max-w-md w-full fade-up">
          <h1 className="font-display font-extrabold text-2xl text-[#0B3D91] mb-1">{t.admin.title}</h1>
          <p className="text-sm text-[#5A6A94] mb-5">{t.admin.tokenLabel}</p>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)}
            className="warm-input" placeholder="Token"
            data-testid="admin-token-input"
            onKeyDown={(e) => e.key === "Enter" && load()} />
          <button onClick={load} disabled={loading || !token} className="btn-primary w-full mt-4" data-testid="admin-login-btn">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <LogIn size={16} />} {t.admin.login}
          </button>
        </div>
      </div>
    );
  }

  const filtered = filter === "all"
    ? regs
    : filter === "todo"
    ? regs.filter((r) => PENDING_STATUSES.has(r.approval_status))
    : regs.filter((r) => r.approval_status === filter);
  const counts = regs.reduce((acc, r) => { acc[r.approval_status] = (acc[r.approval_status] || 0) + 1; return acc; }, {});
  const todoCount = (counts.pending || 0) + (counts.receipt_uploaded || 0);

  // Group filtered iscritti by slot_id (or orario_corso legacy label) for grouped view
  const grouped = filtered.reduce((acc, r) => {
    const key = r.slot_id || `legacy:${r.orario_corso || "senza_orario"}`;
    (acc[key] = acc[key] || []).push(r);
    return acc;
  }, {});
  const groupedKeys = Object.keys(grouped).sort();

  return (
    <div className="py-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-display font-extrabold text-3xl text-[#0B3D91]">{t.admin.title}</h1>
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <span className="text-xs text-[#8494B8]" data-testid="last-refresh">
                Ultimo aggiornamento: {lastRefresh.toLocaleTimeString("it-IT")} · auto ogni 15s
              </span>
            )}
            <button onClick={() => load(false)} className="btn-ghost text-sm" data-testid="admin-refresh-btn">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
            </button>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Iscrizioni totali" value={regs.length} color="brand-blue-bg" />
          <StatCard label="Da approvare" value={todoCount} color="brand-orange-bg" hot={todoCount > 0} />
          <StatCard label="Approvate" value={counts.approved || 0} color="bg-[#16A34A]" />
          <StatCard label="Visitatori totali" value={visitors.total} color="bg-[#0F1E3D]" sub={`${visitors.today} oggi · ${visitors.week} settimana`} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#E4EAF7] overflow-x-auto">
          {[
            { k: "regs", label: `Iscrizioni (${regs.length})` },
            { k: "visitors", label: `Visitatori (${visitors.total})` },
            { k: "slots", label: "Orari Lezioni" },
            { k: "slot-requests", label: "Richieste Slot" },
            { k: "materials", label: "Materiali" },
            { k: "settings", label: "Notifiche" },
          ].map((tb) => (
            <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`tab-${tb.k}`}
              className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap shrink-0 ${
                tab === tb.k ? "brand-blue-bg text-white" : "text-[#5A6A94] hover:text-[#0B3D91]"
              }`}>{tb.label}</button>
          ))}
        </div>

        {tab === "regs" && (
          <div className="card-elevated p-6" data-testid="admin-registrations-section">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-display font-bold text-xl text-[#0B3D91]">Iscrizioni</h2>
              <div className="flex flex-wrap gap-2">
                {[
                  { k: "todo", label: `Da approvare (${todoCount})`, hot: true },
                  { k: "all", label: `Tutte (${regs.length})` },
                  { k: "approved", label: `Approvate (${counts.approved || 0})` },
                  { k: "rejected", label: `Rifiutate (${counts.rejected || 0})` },
                ].map((f) => (
                  <button key={f.k} onClick={() => setFilter(f.k)}
                    data-testid={`filter-${f.k}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                      filter === f.k ? "brand-blue-bg text-white" : "bg-[#EEF4FF] text-[#0B3D91] hover:bg-[#DDE4F5]"
                    } ${f.hot && todoCount > 0 ? "ring-2 ring-[#F97316] animate-pulse" : ""}`}>
                    {f.label}
                  </button>
                ))}
                <button onClick={() => setShowBulk(true)}
                  className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#7C3AED] text-white hover:bg-[#6D28D9] inline-flex items-center gap-1"
                  data-testid="bulk-password-btn"
                  title="Genera password temporanee per tutti gli approvati senza password">
                  <KeyRound size={13} /> Password in blocco
                </button>
                <button onClick={() => setGroupBySlot((v) => !v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1 ${
                    groupBySlot ? "bg-[#0B3D91] text-white" : "bg-[#EEF4FF] text-[#0B3D91] hover:bg-[#DDE4F5]"
                  }`}
                  data-testid="toggle-group-slot">
                  <Clock size={13} /> {groupBySlot ? "Lista piatta" : "Raggruppa per slot"}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#8494B8] uppercase text-xs tracking-wider border-b border-[#E4EAF7]">
                    <th className="py-2 pr-3">Matricola</th>
                    <th className="py-2 pr-3">Nome</th>
                    <th className="py-2 pr-3">Contatti</th>
                    <th className="py-2 pr-3">Orario</th>
                    <th className="py-2 pr-3">Pagamenti</th>
                    <th className="py-2 pr-3">Stato</th>
                    <th className="py-2 pr-3">Data</th>
                    <th className="py-2 pr-3">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {(groupBySlot ? groupedKeys.flatMap((key) => {
                    const items = grouped[key];
                    const slot = slotsMap[key];
                    const header = {
                      _group: true,
                      key,
                      title: slot
                        ? `${slot.time}${slot.label ? " — " + slot.label : ""} (${items.length})`
                        : key.startsWith("legacy:")
                        ? `Senza slot assegnato (${items.length}) · fascia: ${key.replace("legacy:", "") || "—"}`
                        : `Slot cancellato (${items.length})`,
                    };
                    return [header, ...items];
                  }) : filtered).map((r) => (
                    r._group ? (
                      <tr key={`g-${r.key}`} className="bg-[#F6F9FF]" data-testid={`admin-reg-group-${r.key}`}>
                        <td colSpan={8} className="py-2 px-3 font-bold text-[#0B3D91] uppercase text-xs tracking-wider border-y-2 border-[#0B3D91]/20">
                          🕐 {r.title}
                        </td>
                      </tr>
                    ) : (
                    <tr key={r.id} className="border-b border-[#EEF4FF]" data-testid={`admin-reg-row-${r.id}`}>
                      <td className="py-2 pr-3 font-mono font-bold text-[#0B3D91]" data-testid={`matricola-${r.id}`}>{r.matricola || "—"}</td>
                      <td className="py-2 pr-3 font-medium">
                        <div className="inline-flex items-center gap-1.5">
                          <span>{r.nome} {r.cognome}</span>
                          <button onClick={() => renameUser(r.id, r.nome, r.cognome)}
                            className="text-[#8494B8] hover:text-[#0B3D91] p-0.5 rounded"
                            data-testid={`rename-${r.id}`}
                            title="Modifica nome / cognome">
                            <Pencil size={12} />
                          </button>
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        <a href={`https://wa.me/${(r.cellulare || "").replace(/[^\d]/g, "").replace(/^0/, "39")}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-[#25D366] font-semibold inline-flex items-center gap-1 hover:underline">
                          <MessageCircle size={12} /> {r.cellulare}
                        </a>
                      </td>
                      <td className="py-2 pr-3">
                        <select
                          value={r.orario_corso}
                          onChange={(e) => changeSlot(r.id, e.target.value)}
                          className="text-xs bg-[#EEF4FF] text-[#0B3D91] rounded-md px-2 py-1 font-semibold cursor-pointer hover:bg-[#DDE4F5] border-0"
                          data-testid={`slot-select-${r.id}`}
                          title="Cambia orario corso"
                        >
                          <option value="10-11">10-11</option>
                          <option value="18-19">18-19</option>
                          <option value="entrambi">Entrambi ★</option>
                        </select>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-col gap-1 text-xs">
                          <label className={`inline-flex items-center gap-1 cursor-pointer font-semibold ${r.paid_corso ? "text-[#16A34A]" : "text-[#8494B8]"}`} title="Quota corso €30">
                            <input type="checkbox" checked={!!r.paid_corso}
                              onChange={(e) => togglePayment(r.id, "corso", e.target.checked)}
                              className="w-3.5 h-3.5 accent-[#16A34A]"
                              data-testid={`paid-corso-${r.id}`} />
                            <Euro size={11} /> 30 corso
                          </label>
                          <label className={`inline-flex items-center gap-1 cursor-pointer font-semibold ${r.paid_socio ? "text-[#F97316]" : "text-[#8494B8]"}`} title="Quota socio €20">
                            <input type="checkbox" checked={!!r.paid_socio}
                              onChange={(e) => togglePayment(r.id, "socio", e.target.checked)}
                              className="w-3.5 h-3.5 accent-[#F97316]"
                              data-testid={`paid-socio-${r.id}`} />
                            <Euro size={11} /> 20 socio
                          </label>
                        </div>
                      </td>
                      <td className="py-2 pr-3"><StatusBadge s={r.approval_status} /></td>
                      <td className="py-2 pr-3 text-xs text-[#8494B8]">{new Date(r.created_at).toLocaleDateString("it-IT")}</td>
                      <td className="py-2 pr-3">
                        <div className="flex gap-1 flex-wrap">
                          {r.approval_status !== "approved" && (
                            <button onClick={() => approve(r.id)} className="!py-1 !px-2 text-xs rounded-full bg-[#16A34A] text-white hover:bg-[#128A3D] inline-flex items-center gap-1" data-testid={`approve-${r.id}`}>
                              <Check size={14} /> Approva
                            </button>
                          )}
                          {r.approval_status !== "rejected" && r.approval_status !== "approved" && (
                            <button onClick={() => reject(r.id)} className="!py-1 !px-2 text-xs rounded-full bg-[#F97316] text-white hover:bg-[#EA6410] inline-flex items-center gap-1" data-testid={`reject-${r.id}`}>
                              <X size={14} /> Rifiuta
                            </button>
                          )}
                          {r.approval_status === "approved" && (
                            <button onClick={() => downloadPdf(r.id)} className="!py-1 !px-2 text-xs rounded-full bg-[#0B3D91] text-white hover:bg-[#082F76] inline-flex items-center gap-1" data-testid={`pdf-${r.id}`} title="Scarica modulo firmato">
                              <FileDown size={14} /> PDF
                            </button>
                          )}
                          {r.approval_status === "approved" && (
                            <button onClick={() => setUserPassword(r.id, `${r.nome} ${r.cognome}`)} className="!py-1 !px-2 text-xs rounded-full bg-[#7C3AED] text-white hover:bg-[#6D28D9] inline-flex items-center gap-1" data-testid={`set-password-${r.id}`} title="Imposta / resetta password utente">
                              <KeyRound size={14} /> Password
                            </button>
                          )}
                          <button onClick={() => deleteRegistration(r.id, `${r.nome} ${r.cognome}`)} className="!py-1 !px-2 text-xs rounded-full bg-[#DC2626] text-white hover:bg-[#B91C1C] inline-flex items-center gap-1" data-testid={`delete-${r.id}`} title="Cancella iscrizione">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    )
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-[#8494B8]">Nessuna iscrizione</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showBulk && (
          <BulkPasswordModal
            open={showBulk}
            items={bulkItems}
            loading={bulkLoading}
            onGenerate={runBulkGenerate}
            onPrint={printBulk}
            onClose={() => { setShowBulk(false); setBulkItems(null); }}
          />
        )}

        {tab === "visitors" && (
          <div className="card-elevated p-6" data-testid="admin-visitors-section">
            <div className="flex items-center gap-3 mb-4">
              <Users className="text-[#0B3D91]" size={22} />
              <h2 className="font-display font-bold text-xl text-[#0B3D91]">Visitatori dell'app</h2>
            </div>
            <p className="text-xs text-[#8494B8] mb-4">
              Utenti unici anonimi (ID salvato nel loro browser). Non contengono dati personali.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#8494B8] uppercase text-xs tracking-wider border-b border-[#E4EAF7]">
                    <th className="py-2 pr-3">ID</th>
                    <th className="py-2 pr-3">Prima visita</th>
                    <th className="py-2 pr-3">Ultima visita</th>
                    <th className="py-2 pr-3">Pagine viste</th>
                    <th className="py-2 pr-3">Ultima pagina</th>
                    <th className="py-2 pr-3">Lingua</th>
                    <th className="py-2 pr-3">Paese</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.visitors.map((v) => (
                    <tr key={v.anon_id} className="border-b border-[#EEF4FF]" data-testid={`visitor-row-${v.anon_id}`}>
                      <td className="py-2 pr-3 font-mono text-xs">{v.anon_id.slice(-10)}</td>
                      <td className="py-2 pr-3 text-xs text-[#8494B8]">{v.first_seen_at ? new Date(v.first_seen_at).toLocaleString("it-IT") : "-"}</td>
                      <td className="py-2 pr-3 text-xs text-[#8494B8]">{v.last_seen_at ? new Date(v.last_seen_at).toLocaleString("it-IT") : "-"}</td>
                      <td className="py-2 pr-3">
                        <span className="px-2 py-0.5 rounded-full bg-[#EEF4FF] text-[#0B3D91] text-xs font-bold">{v.pages_viewed || 1}</span>
                      </td>
                      <td className="py-2 pr-3 text-xs">{v.last_path || "-"}</td>
                      <td className="py-2 pr-3 text-xs uppercase font-bold text-[#F97316]">{v.last_lang || "-"}</td>
                      <td className="py-2 pr-3 text-xs">{v.country || "-"}</td>
                    </tr>
                  ))}
                  {visitors.visitors.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-[#8494B8]">Nessun visitatore ancora</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "slots" && <SlotsAdmin headers={headers} />}
        {tab === "slot-requests" && <SlotRequestsAdmin headers={headers} />}

        {tab === "materials" && <MaterialsAdmin headers={headers} />}


        {tab === "settings" && (
          <div className="card-elevated p-6 space-y-5" data-testid="admin-settings-section">
            <div className="flex items-center gap-2">
              <Bell className="text-[#F97316]" size={22} />
              <h2 className="font-display font-bold text-xl text-[#0B3D91]">Notifiche nuove iscrizioni</h2>
            </div>
            <p className="text-sm text-[#5A6A94]">
              Quando qualcuno si iscrive, ti avvisiamo subito in <strong>3 modi</strong>:
              (1) 🔔 suono + banner nel browser mentre il pannello è aperto;
              (2) 📱 WhatsApp al numero admin (attivo appena aggiungi le credenziali Meta);
              (3) 📧 email (attiva appena colleghiamo un servizio SMTP).
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-[#0F1E3D] block mb-1">📱 WhatsApp admin (E.164)</label>
                <input value={settings.admin_phone || ""} onChange={(e) => setSettings({ ...settings, admin_phone: e.target.value })}
                  className="warm-input" placeholder="+393520716656" data-testid="admin-phone-input" />
                <p className="text-xs text-[#8494B8] mt-1">Numero che riceverà il ping WhatsApp (una volta collegate le credenziali Meta).</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-[#0F1E3D] block mb-1">📧 Email admin</label>
                <input value={settings.admin_email || ""} onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })}
                  className="warm-input" placeholder="info@sseuropa.com" data-testid="admin-email-input" />
                <p className="text-xs text-[#8494B8] mt-1">Riceverà notifiche via email (in arrivo).</p>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!settings.notify_on_new_registration}
                onChange={(e) => setSettings({ ...settings, notify_on_new_registration: e.target.checked })}
                className="w-4 h-4 accent-[#0B3D91]"
                data-testid="notify-toggle" />
              <span className="text-sm font-semibold text-[#0F1E3D]">Attiva notifica su nuova iscrizione</span>
            </label>

            <div className="flex gap-3">
              <button onClick={saveSettings} className="btn-primary" data-testid="save-settings-btn">
                <Save size={16} /> Salva impostazioni
              </button>
              <button
                onClick={() => {
                  if (typeof Notification !== "undefined") {
                    Notification.requestPermission().then((perm) => {
                      if (perm === "granted") toast.success("Notifiche browser attivate ✓");
                      else toast.error("Permesso negato — abilita nelle impostazioni del browser");
                    });
                  }
                }}
                className="btn-outline"
                data-testid="enable-browser-notif-btn"
              >
                <Bell size={16} /> Attiva notifiche browser
              </button>
            </div>

            <div className="pt-4 border-t border-[#E4EAF7]">
              <div className="flex items-center gap-2 mb-2">
                <SettingsIcon className="text-[#0B3D91]" size={18} />
                <h3 className="font-display font-bold text-lg text-[#0B3D91]">Ricordini di rinnovo (auto)</h3>
              </div>
              <p className="text-sm text-[#5A6A94]">
                Ogni giorno alle <strong>08:00 UTC</strong> il sistema controlla gli abbonamenti che scadono tra 15 giorni e invia automaticamente un ricordino WhatsApp allo studente per il rinnovo. Puoi anche eseguirlo manualmente ora:
              </p>
              <button
                onClick={async () => {
                  try {
                    const { data } = await axios.post(`${API}/admin/renewal-reminders/run`, {}, { headers });
                    toast.success(`Ricordini inviati: ${data.reminded} · Saltati: ${data.skipped} · Falliti/mocked: ${data.failed_or_mocked}`, { duration: 8000 });
                  } catch { toast.error("Errore"); }
                }}
                className="btn-outline mt-3"
                data-testid="run-renewal-btn"
              >
                <RefreshCw size={16} /> Esegui adesso
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color, sub, hot }) {
  return (
    <div className={`card-elevated p-4 flex items-center gap-3 ${hot ? "ring-2 ring-[#F97316]" : ""}`}>
      <div className={`w-11 h-11 rounded-xl ${color} text-white flex items-center justify-center font-bold text-lg`}>{value}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wider text-[#8494B8]">{label}</div>
        {sub && <div className="text-xs text-[#5A6A94]">{sub}</div>}
      </div>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    pending: { label: "Da approvare", cls: "bg-[#FFF7E6] text-[#B45309]" },
    receipt_uploaded: { label: "Da verificare", cls: "bg-[#EEF4FF] text-[#0B3D91]" },
    approved: { label: "Approvata", cls: "bg-[#E8F8EE] text-[#16A34A]" },
    rejected: { label: "Rifiutata", cls: "bg-[#FFF4EC] text-[#F97316]" },
  };
  const v = map[s] || { label: s || "-", cls: "bg-gray-100 text-gray-600" };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${v.cls}`}>{v.label}</span>;
}


function BulkPasswordModal({ open, items, loading, onGenerate, onPrint, onClose }) {
  if (!open) return null;
  const hasItems = Array.isArray(items);
  const printable = hasItems ? items.filter((i) => i.password) : [];
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-[#E4EAF7] flex items-center justify-between">
          <h2 className="font-display font-extrabold text-xl text-[#0B3D91]">Password temporanee in blocco</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#EEF4FF]" aria-label="Chiudi" data-testid="bulk-close">✕</button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {!hasItems ? (
            <div className="text-sm text-[#5A6A94] space-y-3">
              <p>
                Genera una password temporanea di 8 caratteri per <strong>ogni iscrizione approvata senza password</strong>.
                Ogni studente dovrà cambiarla al primo accesso.
              </p>
              <ul className="list-disc pl-5 text-xs text-[#8494B8]">
                <li>Salta gli iscritti che hanno già una password impostata</li>
                <li>Le password vengono mostrate <strong>una sola volta</strong> — stampa o copia subito</li>
                <li>Puoi comunque resettarne una in qualsiasi momento col bottone 🔑 sulla riga</li>
              </ul>
              <button
                onClick={() => onGenerate(true)}
                disabled={loading}
                className="btn-primary mt-2"
                data-testid="bulk-generate-btn"
              >
                {loading ? "Genero..." : "Genera password mancanti"}
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-[#0F1E3D]">
                  Generate <strong>{printable.length}</strong> password · Saltate: <strong>{items.length - printable.length}</strong>
                </div>
                <button onClick={onPrint} disabled={printable.length === 0}
                  className="btn-primary text-xs !py-2 !px-4" data-testid="bulk-print-btn">
                  🖨️ Stampa / scarica
                </button>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#8494B8] uppercase text-xs tracking-wider border-b border-[#E4EAF7]">
                    <th className="py-2 pr-3">Matricola</th>
                    <th className="py-2 pr-3">Nome</th>
                    <th className="py-2 pr-3">Password</th>
                    <th className="py-2 pr-3">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={`${it.matricola}-${it.action}`} className="border-b border-[#EEF4FF]">
                      <td className="py-2 pr-3 font-mono font-bold text-[#0B3D91]">{it.matricola}</td>
                      <td className="py-2 pr-3">{it.nome} {it.cognome}</td>
                      <td className="py-2 pr-3 font-mono font-bold text-[#B45309] bg-[#FFF7ED] px-2 rounded">
                        {it.password || <span className="text-[#8494B8] italic font-normal bg-transparent">—</span>}
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        {it.action === "created" && <span className="text-[#16A34A] font-semibold">Creato</span>}
                        {it.action === "updated" && <span className="text-[#0B3D91] font-semibold">Reimpostata</span>}
                        {it.action === "skipped" && <span className="text-[#8494B8]">{it.error || "Saltato"}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function SlotsAdmin({ headers }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ time: "", label: "", meet_link: "", orario_corso: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/slots`, { headers });
      setList(data || []);
    } catch (e) {
      console.error("Slots load failed:", e);
      toast.error("Errore caricamento orari");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.time.trim() || !form.meet_link.trim()) { toast.error("Orario e link obbligatori"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/slots`, {
        time: form.time,
        label: form.label,
        meet_link: form.meet_link,
        orario_corso: form.orario_corso || null,
      }, { headers });
      toast.success("Orario aggiunto");
      setForm({ time: "", label: "", meet_link: "", orario_corso: "" });
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Errore creazione");
    } finally { setSaving(false); }
  };

  const remove = async (id, time) => {
    if (!window.confirm(`Eliminare l'orario ${time}?`)) return;
    try {
      await axios.delete(`${API}/admin/slots/${id}`, { headers });
      toast.success("Orario eliminato");
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const testLink = (url) => {
    const u = (url || "").trim();
    if (!u) { toast.error("Link vuoto"); return; }
    const final = u.match(/^https?:\/\//i) ? u : `https://${u}`;
    window.open(final, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-6" data-testid="admin-slots-section">
      <div className="card-elevated p-6">
        <div className="flex items-center gap-2 mb-2">
          <PlusCircle className="text-[#F97316]" size={22} />
          <h2 className="font-display font-bold text-xl text-[#0B3D91]">Nuovo orario lezione</h2>
        </div>
        <p className="text-sm text-[#5A6A94] mb-4">
          Lezioni fisse da <strong>lunedì a venerdì</strong>. Aggiungi un orario (es. <code className="text-xs bg-[#EEF4FF] px-1 rounded">10:00-11:00</code>) e il link Google Meet / Zoom. Scegli la <strong>fascia</strong>: ogni studente vedrà solo gli slot della fascia a cui si è iscritto.
        </p>
        <form onSubmit={create} className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold text-[#0F1E3D] block mb-1">Orario *</label>
            <input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
              className="warm-input" placeholder="10:00-11:00" data-testid="slot-time-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#0F1E3D] block mb-1">Fascia *</label>
            <select value={form.orario_corso}
              onChange={(e) => setForm({ ...form, orario_corso: e.target.value })}
              className="warm-input"
              data-testid="slot-orario-input">
              <option value="">Visibile a tutti</option>
              <option value="10-11">Mattina (10-11)</option>
              <option value="18-19">Sera (18-19)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#0F1E3D] block mb-1">Etichetta (opz.)</label>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="warm-input" placeholder="Mattina A1" data-testid="slot-label-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#0F1E3D] block mb-1">Link meeting *</label>
            <input value={form.meet_link} onChange={(e) => setForm({ ...form, meet_link: e.target.value })}
              className="warm-input" placeholder="https://meet.google.com/..." data-testid="slot-link-input" />
          </div>
          <div className="sm:col-span-4">
            <button type="submit" disabled={saving} className="btn-primary" data-testid="slot-create-btn">
              {saving ? <><Loader2 className="animate-spin" size={16} /> Salvo...</> : <><PlusCircle size={16} /> Aggiungi orario</>}
            </button>
          </div>
        </form>
      </div>

      <div className="card-elevated p-6">
        <h3 className="font-display font-bold text-lg text-[#0B3D91] mb-3">Orari pubblicati ({list.length})</h3>
        {loading ? (
          <p className="text-sm text-[#5A6A94]">Caricamento...</p>
        ) : list.length === 0 ? (
          <p className="text-sm text-[#8494B8] italic">Nessun orario pubblicato. Aggiungine uno sopra.</p>
        ) : (
          <div className="divide-y divide-[#EEF4FF]">
            {list.map((s) => (
              <div key={s.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-slot-${s.id}`}>
                <div className="font-display font-bold text-xl text-[#0B3D91] min-w-[110px]">{s.time}</div>
                <div className="flex-1 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    {s.label && <span className="font-semibold text-[#0F1E3D]">{s.label}</span>}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      s.orario_corso === "10-11" ? "bg-[#FFF7E6] text-[#B45309]" :
                      s.orario_corso === "18-19" ? "bg-[#EEF4FF] text-[#0B3D91]" :
                      "bg-[#F0FDF4] text-[#16A34A]"
                    }`}>
                      {s.orario_corso === "10-11" ? "Mattina 10-11" :
                       s.orario_corso === "18-19" ? "Sera 18-19" :
                       "Tutti"}
                    </span>
                  </div>
                  <a href={s.meet_link} target="_blank" rel="noopener noreferrer"
                    className="text-[#0B3D91] break-all underline text-xs">{s.meet_link}</a>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => testLink(s.meet_link)}
                    className="btn-ghost text-xs" data-testid={`slot-test-${s.id}`}>
                    🔗 Testa
                  </button>
                  <button type="button" onClick={() => remove(s.id, s.time)}
                    className="!py-1.5 !px-3 text-xs rounded-full bg-[#DC2626] text-white hover:bg-[#B91C1C] inline-flex items-center gap-1"
                    data-testid={`slot-delete-${s.id}`}>
                    <Trash2 size={13} /> Elimina
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


const MAT_ICONS = { pdf: FileText, pptx: Presentation, video: VideoIcon };
const MAT_TINT = { pdf: "bg-[#DC2626]", pptx: "bg-[#EA580C]", video: "bg-[#0B3D91]" };

// Chunked upload: 8 MB parts sent 4 at a time in parallel (with retry), then finalized server-side.
async function uploadChunked(file, paramsQs, headers, setProgress) {
  const CHUNK = 8 * 1024 * 1024;
  const PARALLEL = 4;
  const totalChunks = Math.ceil(file.size / CHUNK);
  const initRes = await axios.post(
    `${API}/admin/materials/chunked/init?${paramsQs}`,
    { filename: file.name, content_type: file.type || "application/octet-stream", size: file.size },
    { headers }
  );
  const uploadId = initRes.data.upload_id;
  let done = 0;
  let next = 0;
  const sendPart = async (i) => {
    const fd = new FormData();
    fd.append("chunk", file.slice(i * CHUNK, Math.min((i + 1) * CHUNK, file.size)), `part-${i}`);
    for (let attempt = 0; ; attempt++) {
      try {
        await axios.post(`${API}/admin/materials/chunked/${uploadId}/part/${i}`, fd, { headers, timeout: 0 });
        return;
      } catch (e) {
        if (attempt >= 2) throw e;
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
  };
  const worker = async () => {
    while (next < totalChunks) {
      const i = next++;
      await sendPart(i);
      done++;
      setProgress(Math.round((done / totalChunks) * 99));
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, totalChunks) }, worker));
  await axios.post(`${API}/admin/materials/chunked/${uploadId}/finalize`, {}, { headers, timeout: 0 });
  setProgress(100);
}


function SlotRequestsAdmin({ headers }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/slot-requests`, { headers });
      setList(data || []);
    } catch (e) {
      console.error("Slot requests load failed:", e);
      toast.error("Errore caricamento richieste");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const decide = async (reqId, decision) => {
    try {
      await axios.put(`${API}/admin/slot-requests/${reqId}/${decision}`, {}, { headers });
      toast.success(decision === "approve" ? "Richiesta approvata" : "Richiesta rifiutata");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const filtered = list.filter((r) => r.status === tab);

  return (
    <div className="space-y-6" data-testid="admin-slot-requests">
      <div className="card-elevated p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="font-display font-bold text-xl text-[#0B3D91]">Richieste slot extra</h2>
          <div className="flex gap-2">
            {["pending", "approved", "rejected"].map((s) => {
              const n = list.filter((r) => r.status === s).length;
              return (
                <button key={s} onClick={() => setTab(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                    tab === s ? "brand-blue-bg text-white" : "bg-[#EEF4FF] text-[#0B3D91]"
                  }`}
                  data-testid={`slot-req-filter-${s}`}>
                  {s === "pending" ? `In attesa (${n})` : s === "approved" ? `Approvate (${n})` : `Rifiutate (${n})`}
                </button>
              );
            })}
          </div>
        </div>
        {loading ? (
          <p className="text-sm text-[#5A6A94]">Caricamento...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#8494B8] italic">Nessuna richiesta {tab === "pending" ? "in attesa" : tab === "approved" ? "approvata" : "rifiutata"}.</p>
        ) : (
          <div className="divide-y divide-[#EEF4FF]">
            {filtered.map((r) => (
              <div key={r.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`slot-req-${r.id}`}>
                <div className="min-w-[120px]">
                  <div className="font-mono font-bold text-[#0B3D91]">{r.matricola || "—"}</div>
                  <div className="text-xs text-[#8494B8]">{new Date(r.created_at).toLocaleDateString("it-IT")}</div>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-[#0F1E3D]">{r.nome} {r.cognome}</div>
                  <div className="text-sm text-[#5A6A94]">
                    richiede slot <strong className="text-[#0B3D91]">{r.slot?.time || "(cancellato)"}</strong>
                    {r.slot?.label && <> · {r.slot.label}</>}
                  </div>
                  {r.message && <div className="text-xs text-[#8494B8] italic mt-1">"{r.message}"</div>}
                </div>
                {r.status === "pending" ? (
                  <div className="flex gap-2">
                    <button onClick={() => decide(r.id, "approve")}
                      className="!py-1.5 !px-3 text-xs rounded-full bg-[#16A34A] text-white hover:bg-[#15803D] inline-flex items-center gap-1"
                      data-testid={`slot-req-approve-${r.id}`}>
                      <Check size={12} /> Approva
                    </button>
                    <button onClick={() => decide(r.id, "reject")}
                      className="!py-1.5 !px-3 text-xs rounded-full bg-[#DC2626] text-white hover:bg-[#B91C1C] inline-flex items-center gap-1"
                      data-testid={`slot-req-reject-${r.id}`}>
                      <X size={12} /> Rifiuta
                    </button>
                  </div>
                ) : (
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${
                    r.status === "approved" ? "bg-[#E8F8EE] text-[#16A34A]" : "bg-[#FFF4EC] text-[#F97316]"
                  }`}>{r.status === "approved" ? "Approvata" : "Rifiutata"}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function MaterialsAdmin({ headers }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({ title: "", description: "", level: "", orario_corso: "", file: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/materials`, { headers });
      setList(data);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    e.preventDefault();
    if (!form.file || !form.title.trim()) { toast.error("File e titolo obbligatori"); return; }
    const MAX_BYTES = 350 * 1024 * 1024;
    if (form.file.size > MAX_BYTES) {
      toast.error(`File troppo grande: ${(form.file.size / (1024 * 1024)).toFixed(0)} MB. Il limite è 350 MB.`, { duration: 8000 });
      return;
    }
    setUploading(true); setProgress(0);
    const params = new URLSearchParams();
    params.set("title", form.title);
    if (form.description) params.set("description", form.description);
    if (form.level) params.set("level", form.level);
    if (form.orario_corso) params.set("orario_corso", form.orario_corso);
    const sizeMB = form.file.size / (1024 * 1024);
    const useChunked = form.file.size > 16 * 1024 * 1024;
    try {
      if (useChunked) {
        await uploadChunked(form.file, params.toString(), headers, setProgress);
      } else {
        const fd = new FormData();
        fd.append("file", form.file);
        await axios.post(`${API}/admin/materials?${params}`, fd, {
          headers,   // Let axios set the correct multipart boundary automatically.
          timeout: 0, // No timeout — large videos may take a while.
          onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)),
        });
      }
      toast.success(`Materiale caricato (${sizeMB.toFixed(1)} MB)`);
      setForm({ title: "", description: "", level: "", orario_corso: "", file: null });
      load();
    } catch (err) {
      console.error("Upload error:", err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = detail
        ? `Errore ${status || ""}: ${detail}`
        : err?.code === "ECONNABORTED"
        ? "Timeout durante l'upload. Prova con file più piccolo o rete migliore."
        : `Errore upload${status ? ` (HTTP ${status})` : ""}: ${err?.message || "sconosciuto"}`;
      toast.error(msg, { duration: 10000 });
    } finally { setUploading(false); setProgress(0); }
  };

  const [repairing, setRepairing] = useState(false);
  const repair = async () => {
    setRepairing(true);
    try {
      const { data } = await axios.post(`${API}/admin/materials/repair`, {}, { headers });
      if (data.broken.length) toast.warning(`${data.broken.length} file da ricaricare: ${data.broken.map((b) => b.title).join(", ")}`, { duration: 12000 });
      else toast.success(`Tutti i ${data.checked} file sono OK (${data.fixed} sistemati)`);
      load();
    } catch { toast.error("Errore controllo file"); }
    finally { setRepairing(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Cancellare "${m.title}"?`)) return;
    try { await axios.delete(`${API}/admin/materials/${m.id}`, { headers }); toast.success("Cancellato"); load(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="card-elevated p-6 space-y-6" data-testid="admin-materials-section">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Upload className="text-[#F97316]" size={22} />
          <h2 className="font-display font-bold text-xl text-[#0B3D91]">Carica materiale lezione</h2>
        </div>
        <p className="text-sm text-[#5A6A94]">Accettati: PDF, PPT/PPTX, video (MP4/MOV/WebM/MKV). Max 350 MB. File &gt; 16 MB caricati a pezzi in parallelo (più veloce e affidabile). Gli utenti vedranno in modalità sola-visualizzazione con filigrana.</p>
      </div>

      <form onSubmit={upload} className="grid sm:grid-cols-2 gap-3">
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Titolo (es. Lezione 3 — Verbi -are)" className="warm-input" required data-testid="mat-title" />
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Descrizione breve (opzionale)" className="warm-input" data-testid="mat-desc" />
        <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="warm-input" data-testid="mat-level">
          <option value="">Livello (opzionale)</option>
          {["A0", "A1", "A2", "B1", "B2"].map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={form.orario_corso} onChange={(e) => setForm({ ...form, orario_corso: e.target.value })} className="warm-input" data-testid="mat-slot">
          <option value="">Fascia (opzionale — visibile a tutti se vuoto)</option>
          <option value="10-11">Mattina 10-11</option>
          <option value="18-19">Sera 18-19</option>
          <option value="entrambi">Entrambi</option>
        </select>
        <input type="file" accept=".pdf,.ppt,.pptx,.mp4,.mov,.webm,.mkv,.avi" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
          className="warm-input sm:col-span-2" required data-testid="mat-file" />
        <button type="submit" disabled={uploading} className="btn-primary sm:col-span-2 disabled:opacity-50" data-testid="mat-upload-btn">
          {uploading ? <><Loader2 className="animate-spin" size={16} /> Caricamento {progress}%</> : <><Upload size={16} /> Carica</>}
        </button>
      </form>

      <div className="pt-4 border-t border-[#E4EAF7]">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="font-display font-bold text-lg text-[#0B3D91]">Materiali caricati ({list.length})</h3>
          <button type="button" onClick={repair} disabled={repairing} className="btn-outline !py-1.5 !px-3 text-xs disabled:opacity-50" data-testid="mat-repair-btn">
            {repairing ? <Loader2 className="animate-spin" size={14} /> : <Wrench size={14} />} Controlla e ripara file
          </button>
        </div>
        {loading ? <p className="text-[#5A6A94] text-sm">Caricamento...</p>
          : list.length === 0 ? <p className="text-[#8494B8] text-sm text-center py-6">Nessun materiale caricato</p>
          : (
            <div className="space-y-2">
              {list.map((m) => {
                const Icon = MAT_ICONS[m.kind] || FileText;
                const tint = MAT_TINT[m.kind] || "bg-[#0B3D91]";
                return (
                  <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#EEF4FF] hover:bg-[#F9FBFF]" data-testid={`admin-mat-${m.id}`}>
                    <div className={`w-10 h-10 rounded-lg ${tint} text-white flex items-center justify-center shrink-0`}><Icon size={18} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[#0B3D91] truncate">{m.title}</div>
                      {m.broken && <div className="text-xs font-semibold text-[#DC2626]" data-testid={`mat-broken-${m.id}`}>Da ricaricare: {m.broken}</div>}
                      <div className="text-xs text-[#8494B8]">
                        {m.kind.toUpperCase()}{m.level ? ` · ${m.level}` : ""}{m.orario_corso ? ` · ${m.orario_corso}` : ""} · {(m.size / (1024 * 1024)).toFixed(1)} MB · {new Date(m.created_at).toLocaleDateString("it-IT")}
                      </div>
                    </div>
                    <button onClick={() => remove(m)} className="!py-1 !px-2 text-xs rounded-full bg-[#DC2626] text-white hover:bg-[#B91C1C] inline-flex items-center gap-1" data-testid={`mat-delete-${m.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}
